
import {Content} from "antd/es/layout/layout";
import {Select, Spin} from "antd";
import React, {useEffect, useState} from "react";
import {DataTable} from "../DataTable/DataTable";
import {useGeothermal} from "../../hooks/useGeothermal";
import {useOilfield} from "../../hooks/useOilfield";
import {useElements} from "../../hooks/useElements";
import {useCount} from "../../hooks/useCount";
import {COUNTRIES, DEFAULT_COLUMNS, ELEMENTS, ELEMENTS_COLUMNS} from "../../utils/consts";

interface IDataType {
	value: 'geothermal' | 'oilfield' | 'elements',
	label: string,
	data: { fetchData: (where?: string, countries?: string[]) => Promise<any> },
	dataColumns: {label: string, value: string}[];
}

export const DataBase = () => {
	const [data, setData] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const geothermal = useGeothermal(setData);
	const oilfield= useOilfield(setData);
	const elements = useElements(setData);
	const [countries, setCountries] = useState<string[]>([]);
	const [where, setWhere] = useState<string>();
	const { count, fetchCount } = useCount();

	const dataTypes: IDataType[] = [
		{
			value: 'geothermal',
			label: 'Geothermal',
			data: geothermal,
			dataColumns: [...DEFAULT_COLUMNS.map(({label, value}) => ({label, value})), ...ELEMENTS.map((element) => ({label: element.label, value: element.label}))],
		},
		{
			value: 'oilfield',
			label: 'Oilfield',
			data: oilfield,
			dataColumns: [...DEFAULT_COLUMNS.map(({label, value}) => ({label, value})), ...ELEMENTS.map((element) => ({label: element.label, value: element.label}))],
		},
		{
			value: 'elements',
			label: 'Elements',
			data: elements,
			dataColumns: ELEMENTS_COLUMNS,
		}
	];

	const [dataType, setDataType] = useState<IDataType>(dataTypes[0]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const data = await geothermal.fetchData();
				setData(data);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const data = await dataType.data.fetchData(where, countries);
				setData(data);
			} finally {
				setLoading(false);
			}
		};
		if (dataType) {
			fetchData();
			fetchCount(dataType.value, where, countries);
		}
	}, [dataType, where, countries, fetchCount]);

	const updateRows = async (where?: string) => {
		try {
			setLoading(true);
			setWhere(where);
			const data = await dataType.data.fetchData(where, countries);
			setData(data);
			await fetchCount(dataType.value, where, countries);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Content style={{ padding: '0 16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'scroll' }}>
			{/* Compact header row: title + how-it-works link + count badge */}
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', margin: '12px 0 8px', flexWrap: 'wrap', gap: 8 }}>
				<div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
					<h1 style={{ color: 'var(--navy)', fontSize: 22, fontWeight: 700, margin: 0 }}>GeoFluid Data</h1>
					<a href="/how-it-works" style={{ color: 'var(--blue)', fontSize: 13, textDecoration: 'none' }}>How it works?</a>
				</div>
				<div className="db-count-badge" style={{ margin: 0 }}>
					<span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--blue)' }}>Total</span>
					<span className="db-count-value">{count !== null ? count : '—'}</span>
				</div>
			</div>

			{/* Compact filter bar: data type + country on one line */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', marginBottom: 8, flexWrap: 'wrap' }}>
				<span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--navy)' }}>Data</span>
				<Select
					size="small"
					style={{ width: 140 }}
					defaultValue={dataTypes[0]}
					options={dataTypes}
					onChange={async (value) => {
						setDataType(dataTypes.filter((dt) => dt.value === value as unknown as string)[0]);
						await dataTypes.filter((dt) => dt.value === value as unknown as string)[0].data.fetchData();
					}}
				/>
				<span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--navy)' }}>Country</span>
				<Select
					mode="multiple"
					size="small"
					style={{ minWidth: 180, maxWidth: 400, flex: 1 }}
					value={countries}
					disabled={dataType.value === 'elements'}
					options={COUNTRIES}
					onChange={(value) => setCountries(value)}
					placeholder="All"
				/>
			</div>

			{/* Advanced filters + table */}
			<Spin spinning={loading} size="large" style={{width: '100%'}}>
			<DataTable data={data} fetchData={updateRows} alias={'data'} dataColumns={dataType.dataColumns}/>
			</Spin>
		</Content>
	);
}
